/**
 * "Reproducir ahora" from the phone must interrupt the TV **wherever the TV is** — docs/04 §4.4:
 * "`playItem` → *if player not open, navigate to the player with that item*; if open, switch item".
 *
 * The player's own command listener (`usePlayerController`) only exists while the Player screen
 * is mounted, so without this hook a `playItem` sent while the TV sits on Home, Search, Profile
 * or a playlist is silently dropped — which is the normal state at the start of a session, when
 * the first song is always queued from a phone (WP5.2 P1).
 *
 * This listener deliberately does **not** mark the command handled: it only opens the screen.
 * RTDB re-emits the last command node to every new listener, so the player's own subscription
 * receives the same command as it mounts and performs the switch, keeping one code path for
 * "which item plays". `POST /sessions/:sid/play-now` also fronts the item, so the player would
 * pick it as the queue head even if the re-emit were lost.
 */
import type { PlayerCommandNode } from '@akpl/shared';
import { useEffect } from 'react';

import { type RouterApi, selectCurrent } from '../router';
import { selectSid, useStore } from '../store';
import { subscribeCommands } from './commands';

/**
 * Opens the Player screen for a remote `playItem` when it is not already open.
 * Returns true when it navigated (the assertion the unit test makes).
 */
export function openPlayerForCommand(router: RouterApi, command: PlayerCommandNode): boolean {
  if (command.type !== 'playItem') return false;
  if (selectCurrent(router.getState()).screen === 'player') return false;
  /**
   * UX-111: the item id travels with the navigation.
   *
   * Without it the player mounted with no target and fell back to whatever the *queue* already
   * said was playing — which, at the moment the command arrives, is usually nothing yet: the
   * `status: 'playing'` write is a separate RTDB echo. So the first thing the room saw after
   * somebody pressed "Reproducir ahora" was the "Video karaoke" placeholder, and the title, the
   * singer and "Cargando canción…" only appeared once the echo landed. Eight seconds from tap to
   * first frame is what the room actually experiences; the least the TV can do is name the song
   * it is loading for the whole of it.
   */
  router.getState().navigate('player', command.itemId ? { itemId: command.itemId } : undefined);
  return true;
}

export function useRemoteOpenPlayer(router: RouterApi): void {
  const sid = useStore(selectSid);
  const authMode = useStore((s) => s.authMode);

  useEffect(() => {
    if (!sid) return undefined;
    return subscribeCommands(sid, (command) => {
      openPlayerForCommand(router, command);
    });
  }, [sid, authMode, router]);
}
